import { useCallback, useEffect, useState } from "react";
import {
  type StorageUserDetail,
  fetchStorageDetail,
  recalculateStorage,
  runCleanup,
} from "../../api/admin-content";
import { ConfirmDialog } from "../../components/ConfirmDialog";
import {
  LoadingIndicator,
  tableStyles as ts,
} from "../../components/TableParts";
import { t } from "../../i18n";

type ConfirmAction = "recalculate" | "cleanup";

export function StorageDetailPanel({
  userId,
  onActionDone,
}: {
  userId: string;
  onActionDone: () => void;
}): React.JSX.Element {
  const [detail, setDetail] = useState<StorageUserDetail | null>(null);
  const [loading, setLoading] = useState(true);
  const [confirm, setConfirm] = useState<ConfirmAction | null>(null);
  const [msg, setMsg] = useState<string | null>(null);

  useEffect(() => {
    setLoading(true);
    fetchStorageDetail(userId)
      .then(setDetail)
      .catch(() => setDetail(null))
      .finally(() => setLoading(false));
  }, [userId]);

  const handleConfirm = useCallback(
    async (reason: string): Promise<void> => {
      try {
        if (confirm === "recalculate") {
          const r = await recalculateStorage(userId, reason);
          setMsg(
            `Recalculated: ${formatBytes(r.previous_bytes)} -> ${formatBytes(r.recalculated_bytes)}`,
          );
        } else if (confirm === "cleanup") {
          const r = await runCleanup(userId, reason);
          setMsg(`Cleanup: ${r.marked_count} objects marked`);
        }
        onActionDone();
      } catch (e) {
        setMsg(e instanceof Error ? e.message : t("common.action_failed"));
      } finally {
        setConfirm(null);
      }
    },
    [confirm, userId, onActionDone],
  );

  if (loading) return <LoadingIndicator />;
  if (!detail)
    return <p style={ts.emptyText}>{t("storage_detail.failed_to_load")}</p>;

  return (
    <div style={ts.detailPanel}>
      <div style={ts.detailSection}>
        <h4 style={ts.detailTitle}>{t("storage_detail.breakdown")}</h4>
        {detail.breakdown.map((b) => (
          <div key={b.object_type} style={ts.detailRow}>
            <span>{b.object_type}</span>
            <span>
              {b.count} objects, {formatBytes(b.size_bytes)}
            </span>
          </div>
        ))}
      </div>
      {msg && <p style={ts.actionMsg}>{msg}</p>}
      <div style={ts.detailActions}>
        <button
          type="button"
          style={ts.actionBtn}
          onClick={() => setConfirm("recalculate")}
        >
          Recalculate
        </button>
        <button
          type="button"
          style={{ ...ts.actionBtn, ...ts.dangerBtn }}
          onClick={() => setConfirm("cleanup")}
        >
          Run Cleanup
        </button>
      </div>
      <ConfirmDialog
        open={confirm === "recalculate"}
        title={t("storage_detail.recalculate_title")}
        description={t("storage_detail.recalculate_desc")}
        reasonRequired
        onConfirm={handleConfirm}
        onCancel={() => setConfirm(null)}
      />
      <ConfirmDialog
        open={confirm === "cleanup"}
        title={t("storage_detail.cleanup_title")}
        description={t("storage_detail.cleanup_desc")}
        reasonRequired
        onConfirm={handleConfirm}
        onCancel={() => setConfirm(null)}
      />
    </div>
  );
}

function formatBytes(bytes: number): string {
  if (bytes === 0) return "0 B";
  const units = ["B", "KB", "MB", "GB"];
  const i = Math.min(
    Math.floor(Math.log(bytes) / Math.log(1024)),
    units.length - 1,
  );
  const value = bytes / 1024 ** i;
  return `${value.toFixed(i === 0 ? 0 : 1)} ${units[i]}`;
}
